//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MediaImportItemIngestionSource } from '@apis/content-library/contentLibraryApiModel';
import Button from '@components/atoms/button/Button';
import { DefaultButtonStyles } from '@components/atoms/button/buttonStyles';
import { Upload } from '@components/molecules/upload/Upload';
import { ContentDrawerTrigger } from '@content-drawer/ContentDrawerTrigger';
import { ContentImportOptionsDropdown } from '@content-drawer/ContentImportOptionsDropdown';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { DropboxFile } from '@content-drawer/dropbox/dropboxModels';
import { useShouldHideContentDrawerRoutesConfig } from '@content-drawer/hooks/useShouldHideContentDrawerRoutesConfig';
import { getIsDropboxImportsEnabled } from '@content-drawer/redux/getContentDrawer';
import { useUploader } from '@content-drawer/upload/useUploader';
import { useImportFromUrl } from '@hooks/useImportFromUrl';
import { getCollectionsSettingsService } from '@media-library/components/collections/CollectionsService';
import CollectionId from '@models/CollectionId';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { SUPPORTED_MIME_TYPES } from '@utils/mimeTypes';

export const UNIVERSAL_FILE_INPUT_ID = 'file-input-universal-overlay';

type ContentImportOptionsTrigger =
  | 'content-drawer-trigger'
  | 'content-drawer-upload'
  | 'legacy-content-drawer-upload';

export interface ContentImportOptionsLabelProps {
  /**
   * triggerType decides which icon/button to show as the trigger.
   * Example: We show a "+" icon in the top-navigation and an "Upload" button in the content drawer.
   */
  triggerType: ContentImportOptionsTrigger;
}

// If the Content Drawer is disabled we are using a label to trigger the `UNIVERSAL_FILE_INPUT_ID` input
export const ContentImportOptions = ({
  triggerType,
}: ContentImportOptionsLabelProps) => {
  const { importMediaItems } = useImportFromUrl({
    shouldBroadcastEvents: true,
  });
  const { uploadFile } = useUploader();
  const labelRef = useRef<HTMLLabelElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const isRouteWithHiddenContentDrawerTrigger =
    useShouldHideContentDrawerRoutesConfig();
  const collectionsSettingsService = getCollectionsSettingsService();

  const isDamImportEnabledForClient = useSelector(getIsDropboxImportsEnabled);

  const handleOnUpload = () => {
    if (triggerType === 'content-drawer-upload') {
      uploadRef.current?.click();
    } else {
      labelRef.current?.click();
    }
  };

  const handleOnDropboxImport = (files: DropboxFile[]) => {
    // TODO: PD-247216 - Remove the use of collectionsSettingsService once we have migrated collections to react
    const collectionId = collectionsSettingsService.getTenantedCollectionId();
    void importMediaItems(
      files.map((file) => ({
        collectionIds: collectionId ? [CollectionId(collectionId)] : undefined,
        metadata: {
          ingestionSource: MediaImportItemIngestionSource.Dropbox,
          ingestionSourceItemId: file.id,
          name: file.name,
        },
        url: file.link,
      })),
    );
  };

  const onUploadFiles = async (files: FileList | null) => {
    if (files) {
      const list = Array.from(files);
      list.forEach((file) => uploadFile(file));
    }
  };

  const handleOnUploadClick = () => {
    if (!isDamImportEnabledForClient) {
      handleOnUpload();
    }
  };

  const getTrigger = () => {
    switch (triggerType) {
      case 'content-drawer-trigger':
        return isRouteWithHiddenContentDrawerTrigger ? (
          <></>
        ) : (
          <ContentDrawerTrigger onClick={handleOnUpload} />
        );
      case 'legacy-content-drawer-upload':
        return (
          <Button buttonStyles={DefaultButtonStyles} onClick={handleOnUpload}>
            Upload
          </Button>
        );
      case 'content-drawer-upload':
        return (
          <Upload
            acceptableFiles={SUPPORTED_MIME_TYPES}
            handleChange={onUploadFiles}
            isDisabled={false}
            onClick={handleOnUploadClick}
            ref={uploadRef}
            shouldAcceptMultipleFiles={true}
            trackingId={ContentDrawerTrackingIds.UPLOAD_MEDIA}
          />
        );
    }
  };

  const label = (
    <ButtonWrapper>
      <Label ref={labelRef} htmlFor={UNIVERSAL_FILE_INPUT_ID} />
      {getTrigger()}
    </ButtonWrapper>
  );

  return isDamImportEnabledForClient ? (
    <ContentImportOptionsDropdown
      handleOnDropboxImport={handleOnDropboxImport}
      handleOnUpload={handleOnUpload}
    >
      {label}
    </ContentImportOptionsDropdown>
  ) : (
    label
  );
};

const ButtonWrapper = styled.span``;

const Label = styled.label(
  ({ theme }) => `
    margin: ${theme.spacings[0]};
    padding: ${theme.spacings[0]}
  `,
);

// TODO PD-247176 - Remove the use of below component after we remove the legacy content drawer
angular.module('curalate').component('contentImportOptionsButton', {
  controller: makeAngularReactController(
    <ContentImportOptions triggerType='legacy-content-drawer-upload' />,
  ),
});
